import { useState, useCallback } from 'react';
import { UseFormReset, UseFormSetValue, UseFormSetError } from 'react-hook-form';
import { useToast } from '@crisellevaldez/speculo-ui';
import type { User } from 'firebase/auth';
import { Contact } from '../../types/contact.types';
import { Organization } from '../../types/organization.types';
import { ContactFormData } from '../../schemas/contact.schema';
import { useContacts } from '../useContacts';
import { useOrganizationUsers, FormattedUser } from '../useOrganizationUsers';
import { useOrganizationStore } from '../../stores/useOrganizationStore';
import { useAuthStore } from '../../contexts/auth/useAuthStore';
import { giveBackToRemi, optInToFlywheel, optOutOfFlywheel } from '../../api/handoff';

interface UseContactDataProps {
  contact: Contact | null | { id: string };
  reset: UseFormReset<ContactFormData>;
  setValue: UseFormSetValue<ContactFormData>;
  setError: UseFormSetError<ContactFormData>;
  onSuccess?: (data: ContactFormData) => void;
}

interface UseContactDataReturn {
  // State
  fullContact: Contact | null;
  setFullContact: React.Dispatch<React.SetStateAction<Contact | null>>;
  isLoading: boolean;
  isSubmitting: boolean;
  sessionText: string;
  setSessionText: React.Dispatch<React.SetStateAction<string>>;

  // Organization & User
  organization: Organization | null;
  user: User | null;
  users: FormattedUser[];
  loadingTeamMembers: boolean;

  // Email setup helpers
  canSetupEAlerts: boolean;

  // Contact ID
  contactId: string | null;

  // Handlers
  fetchAndSetContact: () => Promise<void>;
  handleClaimedLeadChange: (value: string | string[]) => void;
  handleFormSubmit: (
    data: ContactFormData,
    dirtyFields?: Partial<Record<keyof ContactFormData, unknown>>
  ) => Promise<void>;
  handleGiveBackToRemi: () => Promise<void>;
  handleOptInChange: (checked: boolean) => Promise<void>;
  getSelectedTeamMember: () => string;
  handleTagsChange: (newTags: string[]) => void;

  // Contacts hooks
  optOutContact: (contactId: string) => Promise<void>;
}

export const useContactData = ({
  contact,
  reset,
  setValue,
  setError,
  onSuccess,
}: UseContactDataProps): UseContactDataReturn => {
  const [fullContact, setFullContact] = useState<Contact | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionText, setSessionText] = useState('');

  const { users, loading: loadingTeamMembers } = useOrganizationUsers();
  const { organization } = useOrganizationStore();
  const { user } = useAuthStore();
  const { showToast } = useToast();

  const { editContact, optOutContact, getContactById } = useContacts();

  // Contact ID helper
  const contactId = contact ? ('id' in contact ? contact.id : null) : null;

  // Check if organization has required email configuration for eAlerts
  const hasEmailSetup = Boolean(
    organization?.domain || organization?.emailSetup?.custom_email_domain
  );
  const hasDnsConfigured = Boolean(organization?.dns_configured);
  const canSetupEAlerts = hasEmailSetup && hasDnsConfigured;

  // Function to update the form with contact data
  const updateFormWithContact = useCallback(
    (contactData: Contact) => {
      // Parse the opt_in_date to YYYY-MM-DD format for the date input
      let formattedOptInDate = '';
      if (contactData.opt_in_date) {
        try {
          let dateObj;

          // Check if it's a Firestore timestamp object
          if (
            typeof contactData.opt_in_date === 'object' &&
            contactData.opt_in_date !== null &&
            'seconds' in contactData.opt_in_date &&
            'nanoseconds' in contactData.opt_in_date
          ) {
            // Convert Firestore timestamp to Date
            const timestamp = contactData.opt_in_date as { seconds: number; nanoseconds: number };
            dateObj = new Date(timestamp.seconds * 1000);
          } else {
            // Handle other formats
            dateObj = new Date(contactData.opt_in_date);
          }

          // Check if it's a valid date
          if (!isNaN(dateObj.getTime())) {
            // Format as YYYY-MM-DD
            formattedOptInDate = dateObj.toISOString().split('T')[0];
          } else {
            console.error('Invalid date:', contactData.opt_in_date);
          }
        } catch (error) {
          console.error('Error parsing date:', error);
        }
      }

      reset({
        firstName: contactData.firstName || '',
        lastName: contactData.lastName || '',
        phoneNumber: contactData.phoneNumber || '',
        phoneStatus: contactData.phoneStatus || '',
        email: contactData.email || '',
        address: {
          street: contactData.address?.street || '',
          city: contactData.address?.city || '',
          state: contactData.address?.state || '',
          zip: contactData.address?.zip || '',
        },
        opt_in: contactData.opt_in || false,
        opt_in_date: formattedOptInDate,
        opt_in_desc: contactData.opt_in_desc || '',
        leadSource: contactData.leadSource || '',
        call_tester: contactData.call_tester || false,
        tags: contactData.tags || [],
        claimedLead: contactData.claimedLead,
      });
    },
    [reset]
  );

  // Function to fetch contact data and update the form
  const fetchAndSetContact = useCallback(async () => {
    if (contact && 'id' in contact) {
      // If the contact already has full data (e.g. from GraphQL), use it directly
      if ('firstName' in contact) {
        setFullContact(contact as Contact);
        updateFormWithContact(contact as Contact);
        return;
      }
      // Otherwise fetch from Firestore
      try {
        setIsLoading(true);
        const contactData = await getContactById(contact.id);
        setFullContact(contactData as Contact);
        updateFormWithContact(contactData as Contact);
      } catch (error) {
        console.error('Error fetching contact data:', error);
        showToast({
          message: 'Contact not found. The contact might have been deleted.',
          variant: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    }
  }, [contact, getContactById, showToast, updateFormWithContact]);

  // Function to handle assigned-agent (CRM owner) selection.
  // DUAL WRITE on contact edit: we persist both the new `assignedAgentId`/`assignedAgent`
  // pair AND the legacy `claimedLead`/`claimedLeadSource` pair. Rationale: some backend
  // paths still read `claimed_lead` (flywheel, call router, CRM outbound precedence),
  // so until those migrate we have to keep the legacy shape populated in lockstep.
  // Kept name `handleClaimedLeadChange` for minimal churn at callsites.
  const handleClaimedLeadChange = useCallback(
    (value: string | string[]) => {
      const phoneValue = Array.isArray(value) ? value[0] : value;

      if (!phoneValue) {
        setValue('assignedAgentId', null, { shouldDirty: true });
        setValue('assignedAgent', null, { shouldDirty: true });
        setValue('claimedLead', null, { shouldDirty: true });
        setValue('claimedLeadSource', null, { shouldDirty: true });
        if (fullContact) {
          setFullContact({
            ...fullContact,
            assignedAgentId: null,
            assignedAgent: null,
            claimedLead: null,
            claimedLeadSource: null,
          });
        }
        return;
      }

      const selectedTeamMember = users.find((user) => user.phone_number === phoneValue);

      if (selectedTeamMember) {
        const assignedAgentSnapshot = {
          agentId: selectedTeamMember.id,
          firstName: selectedTeamMember.firstName,
          lastName: selectedTeamMember.lastName,
          email: selectedTeamMember.email ?? null,
          phone: selectedTeamMember.phone_number ?? null,
          photoUrl: selectedTeamMember.photoUrl ?? null,
        };

        // Legacy claimedLead snapshot — mirrors the historical shape other
        // backend services still read. Kept in sync with assignedAgent.
        const claimedLeadSnapshot = {
          name: `${selectedTeamMember.firstName} ${selectedTeamMember.lastName}`.trim(),
          phoneNumber: selectedTeamMember.phone_number,
          id: selectedTeamMember.id,
          email: selectedTeamMember.email,
          bio: selectedTeamMember.bio || '',
          photo_url: selectedTeamMember.photoUrl || '',
        };

        setValue('assignedAgentId', selectedTeamMember.id, { shouldDirty: true });
        setValue('assignedPondId', null, { shouldDirty: true });
        setValue('assignedAgent', assignedAgentSnapshot, { shouldDirty: true });
        setValue('claimedLead', claimedLeadSnapshot, { shouldDirty: true });
        setValue('claimedLeadSource', 'manual_claim', { shouldDirty: true });

        if (fullContact) {
          setFullContact({
            ...fullContact,
            assignedAgentId: selectedTeamMember.id,
            assignedPondId: null,
            assignedAgent: assignedAgentSnapshot,
            claimedLead: claimedLeadSnapshot,
            claimedLeadSource: 'manual_claim',
          });
        }
      } else {
        setValue('assignedAgentId', null, { shouldDirty: true });
        setValue('assignedAgent', null, { shouldDirty: true });
        setValue('claimedLead', null, { shouldDirty: true });
        setValue('claimedLeadSource', null, { shouldDirty: true });
        if (fullContact) {
          setFullContact({
            ...fullContact,
            assignedAgentId: null,
            assignedAgent: null,
            claimedLead: null,
            claimedLeadSource: null,
          });
        }
      }
    },
    [setValue, users, fullContact, setFullContact]
  );

  // Handle form submission
  const handleFormSubmit = useCallback(
    async (
      data: ContactFormData,
      dirtyFields?: Partial<Record<keyof ContactFormData, unknown>>
    ) => {
      const contactToUse = fullContact || contact;
      if (!contactToUse || !('id' in contactToUse)) return;

      try {
        setIsSubmitting(true);

        // Only send fields that changed — dirtyFields is truthy for scalar fields
        // and an object/array for nested fields like address / tags / claimedLead.
        // Arrays and objects are always truthy in JS (even [] or {}), so we need a deep check.
        const isFieldDirty = (val: unknown): boolean => {
          if (val === true) return true;
          if (Array.isArray(val)) return val.some(isFieldDirty);
          if (typeof val === 'object' && val !== null) return Object.values(val).some(isFieldDirty);
          return false;
        };
        const dataToSave =
          dirtyFields && Object.keys(dirtyFields).length > 0
            ? (Object.keys(dirtyFields) as (keyof ContactFormData)[]).reduce((acc, key) => {
                if (isFieldDirty(dirtyFields[key])) {
                  (acc as Record<string, unknown>)[key] = data[key];
                }
                return acc;
              }, {} as Partial<ContactFormData>)
            : { ...data };

        await editContact(contactToUse.id, dataToSave as ContactFormData);

        // Update fullContact with saved data so the UI reflects changes immediately
        if (fullContact) {
          setFullContact({
            ...fullContact,
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber || '',
            phoneStatus: data.phoneStatus || '',
            email: data.email || '',
            address: data.address || { street: '', city: '', state: '', zip: '' },
            opt_in: data.opt_in || false,
            opt_in_date: data.opt_in_date || '',
            opt_in_desc: data.opt_in_desc || '',
            leadSource: data.leadSource || '',
            call_tester: data.call_tester || false,
            tags: data.tags || [],
            claimedLead: data.claimedLead || undefined,
          });
        }

        // Call onSuccess if provided
        onSuccess?.(data);

        showToast({
          message: 'Contact updated successfully',
          variant: 'success',
        });
      } catch (error) {
        console.error('Error updating contact:', error);
        if (error instanceof Error) {
          if (error.message === 'Phone number already exists') {
            setError('phoneNumber', {
              type: 'manual',
              message: 'Phone number already exists',
            });
          } else if (error.message === 'Email already exists') {
            setError('email', {
              type: 'manual',
              message: 'Email already exists',
            });
          } else {
            setError('root', {
              type: 'manual',
              message: error.message,
            });
          }
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [fullContact, contact, editContact, onSuccess, showToast, setError]
  );

  // Handle give back to Remi (release the active handoff and resume nurture)
  const handleGiveBackToRemi = useCallback(async () => {
    if (contactId && fullContact?.activeHandoffId && organization?.id && user?.uid) {
      try {
        // No agentId: the backend attributes the action to the authenticated
        // caller when it is absent. Sending one only creates a chance to
        // disagree — `_resolve_actor_agent_id` 403s a non-admin whose payload
        // agentId differs from their token identity, and these sites sent the
        // Firebase uid while the handoff queue claims with the TeamMember id.
        // Two spellings of the same person, one of which loses.
        //
        // Ownership is unaffected: a separate guard compares the row's
        // target_agent_id with the resolved caller and still rejects giving
        // back a lead someone else holds.
        const result = await giveBackToRemi({
          organizationId: organization.id,
          contactId,
          handoffId: fullContact.activeHandoffId,
        });
        if (result.success) {
          showToast({
            message: `Lead returned to ${organization?.assistant_name || 'Remi'}. Automated follow-ups resumed.`,
            variant: 'success',
          });
          // Update local state
          if (fullContact) {
            setFullContact({
              ...fullContact,
              claimedLead: undefined,
              hasClaimedLead: false,
              activeHandoffId: null,
              flywheelOptOut: false,
            });
          }
        } else {
          showToast({
            message: result.error || 'Failed to return lead to Remi',
            variant: 'error',
          });
        }
      } catch (error) {
        console.error('Error opting into flywheel:', error);
        showToast({
          message: 'Failed to return lead to Remi. Please try again.',
          variant: 'error',
        });
      }
    }
  }, [contactId, organization, user, fullContact, showToast]);

  const handleOptInChange = useCallback(
    async (checked: boolean) => {
      if (!contactId || !organization?.id || !user?.uid) {
        throw new Error('Missing contact, organization, or user');
      }

      if (checked) {
        const result = await optInToFlywheel({
          organizationId: organization.id,
          contactId,
          agentId: user.uid,
        });

        if (!result.success) {
          throw new Error(result.error || 'Failed to opt in contact');
        }

        setValue('opt_in', true, { shouldDirty: false });
        if (fullContact) {
          setFullContact({
            ...fullContact,
            opt_in: true,
            flywheelOptOut: false,
            flywheelOptOutAt: undefined,
          });
        }

        showToast({
          message: 'Contact opted in successfully',
          variant: 'success',
        });
        return;
      }

      const result = await optOutOfFlywheel({
        organizationId: organization.id,
        contactId,
        agentId: user.uid,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to opt out contact');
      }

      setValue('opt_in', false, { shouldDirty: false });
      if (fullContact) {
        setFullContact({
          ...fullContact,
          opt_in: false,
          flywheelOptOut: true,
        });
      }

      showToast({
        message: 'Contact opted out successfully',
        variant: 'success',
      });
    },
    [contactId, organization, user, fullContact, setValue, showToast]
  );

  // The team member this contact is assigned to. Claiming a lead is not owning
  // it, so the old fallbacks through claimedLead's phone and id are gone: they
  // preselected whoever last claimed the contact, disagreeing with the
  // Assigned To column, which has always read assignedAgentId alone.
  const getSelectedTeamMember = useCallback(() => {
    if (!fullContact?.assignedAgentId) return '';
    const match = users.find((u) => u.id === fullContact.assignedAgentId);
    return match ? match.phone_number : '';
  }, [fullContact, users]);

  // Handle tag changes
  const handleTagsChange = useCallback(
    (newTags: string[]) => {
      setValue('tags', newTags, { shouldDirty: true });
    },
    [setValue]
  );

  return {
    // State
    fullContact,
    setFullContact,
    isLoading,
    isSubmitting,
    sessionText,
    setSessionText,

    // Organization & User
    organization,
    user,
    users,
    loadingTeamMembers,

    // Email setup helpers
    canSetupEAlerts,

    // Contact ID
    contactId,

    // Handlers
    fetchAndSetContact,
    handleClaimedLeadChange,
    handleFormSubmit,
    handleGiveBackToRemi,
    handleOptInChange,
    getSelectedTeamMember,
    handleTagsChange,

    // Contacts hooks
    optOutContact,
  };
};
